import React from 'react';
import { Popover } from 'antd';
import IconFont from '$/components/IconFont';
import { useLanguageStore } from '$/store/languageStore';
import { handleImageUrl } from '$/utils/image';
import type { RoleGenFormValues, RoleGenMaterialCategory } from '../roleFormTypes';
import styles from './index.module.scss';

const ROLE_MATERIAL_THUMB_SIZE = 60;

export interface RoleMaterialRowConfig {
  key: 'clothingMaterialId' | 'hairstyleMaterialId' | 'accessoryMaterialId';
  nameKey: 'clothingMaterialName' | 'hairstyleMaterialName' | 'accessoryMaterialName';
  coverKey: 'clothingMaterialCoverUrl' | 'hairstyleMaterialCoverUrl' | 'accessoryMaterialCoverUrl';
  category: RoleGenMaterialCategory;
  label: string;
  placeholder: string;
  required?: boolean;
}

interface RoleMaterialRowProps {
  row: RoleMaterialRowConfig;
  values: RoleGenFormValues;
  uploading?: boolean;
  uploadPopoverOpen?: boolean;
  popoverZIndex: number;
  onSelectMaterial: (category: RoleGenMaterialCategory) => void;
  onPreviewMaterial: (category: RoleGenMaterialCategory) => void;
  onClearMaterial: (category: RoleGenMaterialCategory) => void;
  onLocalUpload: (category: RoleGenMaterialCategory) => void;
  onHistoryUpload: (category: RoleGenMaterialCategory) => void;
  onUploadPopoverOpenChange: (category: RoleGenMaterialCategory, nextOpen: boolean) => void;
}

/**
 * 渲染角色素材通用上传来源菜单，供参考图和素材行复用。
 *
 * @param props 本地上传与历史记录入口
 * @returns 上传菜单节点
 */
export function RoleUploadMenu({
  onLocalUpload,
  onHistoryUpload,
}: {
  onLocalUpload: () => void;
  onHistoryUpload: () => void;
}): React.ReactElement {
  const languageStore = useLanguageStore((state) => state.languageStore);

  return (
    <div className={styles.materialUploadMenu}>
      <button
        type="button"
        className={styles.materialUploadMenuItem}
        onClick={onLocalUpload}
      >
        <IconFont name="uploadBold" fontSize={16} color="currentColor" />
        <span>{languageStore.smartcatTranslationText((value) => value.roboneo_web_local_upload)}</span>
      </button>
      <button
        type="button"
        className={styles.materialUploadMenuItem}
        onClick={onHistoryUpload}
      >
        <IconFont name="clockBold" fontSize={16} color="currentColor" />
        <span>{languageStore.smartcatTranslationText((value) => value.upload_from_history)}</span>
      </button>
    </div>
  );
}

/**
 * 创建角色参考素材单行，负责展示主图、素材名称、官方选择和上传入口。
 *
 * @param props 见 RoleMaterialRowProps
 * @returns 素材行节点
 */
function RoleMaterialRow({
  row,
  values,
  uploading,
  uploadPopoverOpen,
  popoverZIndex,
  onSelectMaterial,
  onPreviewMaterial,
  onClearMaterial,
  onLocalUpload,
  onHistoryUpload,
  onUploadPopoverOpenChange,
}: RoleMaterialRowProps): React.ReactElement {
  const languageStore = useLanguageStore((state) => state.languageStore);
  const uploadButton = (
    <button
      type="button"
      className={styles.materialUploadButton}
      disabled={uploading}
    >
      {!uploading && <IconFont name="uploadBold" fontSize={16} color="currentColor" />}
      <span>
        {uploading
          ? languageStore.smartcatTranslationText((value) => value.upload_progress)
          : languageStore.smartcatTranslationText((value) => value.ai_enhance_upload)}
      </span>
    </button>
  );

  return (
    <div className={styles.materialRow}>
      <div className={styles.materialThumb}>
        <button
          type="button"
          className={styles.materialPreviewButton}
          disabled={!values[row.coverKey]}
          onClick={() => onPreviewMaterial(row.category)}
        >
          {values[row.coverKey] ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={handleImageUrl(values[row.coverKey], { size: ROLE_MATERIAL_THUMB_SIZE })}
              alt={values[row.nameKey] || row.label}
              crossOrigin="anonymous"
            />
          ) : (
            <IconFont name="folderBold" fontSize={22} color="currentColor" />
          )}
        </button>
        {values[row.coverKey] && (
          <button
            type="button"
            className={styles.materialDeleteButton}
            onClick={(event) => {
              event.stopPropagation();
              onClearMaterial(row.category);
            }}
          >
            <IconFont name="trashCanBold" fontSize={14} color="currentColor" />
          </button>
        )}
      </div>
      <div className={styles.materialBody}>
        <div className={styles.materialLabel}>
          {row.label}
          {row.required && <span className={styles.requiredMark}>*</span>}
        </div>
        <div className={styles.materialControl}>
          <button
            type="button"
            className={`${styles.materialValueButton} ${values[row.nameKey] ? '' : styles.materialValuePlaceholder}`}
            onClick={() => onSelectMaterial(row.category)}
          >
            {values[row.nameKey] || row.placeholder}
          </button>
          {uploading ? uploadButton : (
            <Popover
              trigger="click"
              placement="rightTop"
              content={(
                <RoleUploadMenu
                  onLocalUpload={() => onLocalUpload(row.category)}
                  onHistoryUpload={() => onHistoryUpload(row.category)}
                />
              )}
              overlayClassName={styles.materialUploadPopover}
              zIndex={popoverZIndex}
              open={uploadPopoverOpen}
              onOpenChange={(nextOpen) => onUploadPopoverOpenChange(row.category, nextOpen)}
            >
              {uploadButton}
            </Popover>
          )}
        </div>
      </div>
    </div>
  );
}

export default RoleMaterialRow;
